import { DateTime } from "luxon";
import moment from "moment";

import { PredefinedAppointmentGroup } from "@/Appointments/Models/Scheduling/PredefinedAppointmentGroup";
import { PredefinedAppointmentItem } from "@/Appointments/Models/Scheduling/PredefinedAppointmentItem";
import { GuidHelper } from "@/Common/Helpers/GuidHelper";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { SearchMenuHelper } from "@/Common/Helpers/SearchMenuHelper";
import { Person } from "@/Common/Models/Person/Person";
import { IComboOrServiceItem } from "@/Common/Models/Services/BaseServiceDefinitionModel";
import { ServiceComboDefinitionModel } from "@/Common/Models/Services/ServiceComboDefinitionModel";
import { ServiceDefinitionModel } from "@/Common/Models/Services/ServiceDefinitionModel";
import { ServiceOrComboTableItem } from "@/Common/Models/Services/ServiceOrComboTableItem";
import { EmployeeUIModel } from "@/Common/Models/UI/Employee/EmployeeUIModel";
import { CheckboxTarget } from "@/Common/Models/UI/Inputs/CheckboxTarget";
import { CheckboxUIModelT } from "@/Common/Models/UI/Inputs/CheckboxUIModel";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { BaseOperationHandler } from "@/Common/Stores/Handlers/Local/BaseOperationHandler";
import { LuxonDateTimeParser } from "@/Common/Utilities/DateAndTime/LuxonDateTimeParser";
import { PredefinedAppointmentSaveState, PredefinedAppointmentType } from "@/Patient/Components/Appointment/PredefinedAppointmentSave";
import { AnyServiceModel } from "@/Patient/Models/Appointment/AnyServiceModel";
import { SeacrhCriteriaItem } from "@/Patient/Models/Appointment/SearchCriteriaItem";
import { SearchCriteriaListItem } from "@/Patient/Models/Appointment/SearchCriteriaListItem";
import { IPatientAppointmentModel } from "@/Patient/Models/UI/Appointment/IPatientAppointmentModel";
import { PatientStore } from "@/Patient/Stores/PatientStore";

class PatientAppointmentHandler extends BaseOperationHandler {
    private helper: MyleHelper;
    private defaultClinician: Person;

    constructor() {
        super();

        this.helper = new MyleHelper();

        this.RegisterOperation(LocalPatientOperations.Appointment.ShowServiceFinder, this.ShowServiceFinder);
        this.RegisterOperation(LocalPatientOperations.Appointment.HideServiceFinder, this.HideServiceFinder);
        this.RegisterOperation(LocalPatientOperations.Appointment.ChangeTabToService, this.ChangeTabToService);
        this.RegisterOperation(LocalPatientOperations.Appointment.ChangeTabToStaff, this.ChangeTabToStaff);
        this.RegisterOperation(LocalOperations.Misc.ToggleMyleSchedulerDisplayMode, this.ToggleMyleSchedulerDisplayMode);
        this.RegisterOperation(LocalPatientOperations.Appointment.ClinicianSearchChanged, this.ClinicianFilterChanged);
        this.RegisterOperation(LocalPatientOperations.Appointment.OnSearchTabSelected, this.OnSearchTabSelected);
        this.RegisterOperation(LocalPatientOperations.Appointment.OnClinicianTab_ClinicianSelected, this.OnClinicianTab_ClinicianSelected);
        this.RegisterOperation(LocalPatientOperations.Appointment.ServiceSearchChanged, this.ServiceSearchChanged);
        this.RegisterOperation(LocalPatientOperations.Appointment.OnClick_Services, this.OnClick_Services);
        this.RegisterOperation(LocalPatientOperations.Appointment.OnClick_ServiceTabAllEmployee, this.OnClick_ServiceTabAllEmployee);
        this.RegisterOperation(LocalPatientOperations.Appointment.OnOkClick, this.OnOkClick);
        this.RegisterOperation(LocalPatientOperations.Appointment.OnClick_ServicesClinician, this.OnClick_ServicesClinician);
        this.RegisterOperation(LocalPatientOperations.Appointment.Remove_SearchCriteria, this.Remove_SearchCriteria);
        this.RegisterOperation(LocalPatientOperations.Appointment.OpenPredefinedAppointmentTimeSlot, this.OpenPredefinedAppointmentTimeSlot);
        this.RegisterOperation(LocalPatientOperations.Appointment.CancelSavePredefinedAppointment, this.CancelSavePredefinedAppointment);
        this.RegisterOperation(LocalPatientOperations.Appointment.ResetClick, this.ResetClick);

        this.RegisterOperation(LocalPatientOperations.Appointment.Reload, this.Reload);
        this.RegisterOperation(LocalPatientOperations.Appointment.Next, this.Next);
        this.RegisterOperation(LocalPatientOperations.Appointment.Previous, this.Previous);
        this.RegisterOperation(LocalPatientOperations.Appointment.MondayClick, this.MondayClick);
        this.RegisterOperation(LocalPatientOperations.Appointment.TuesdayClick, this.TuesdayClick);
        this.RegisterOperation(LocalPatientOperations.Appointment.WednesdayClick, this.WednesdayClick);
        this.RegisterOperation(LocalPatientOperations.Appointment.ThursdayClick, this.ThursdayClick);
        this.RegisterOperation(LocalPatientOperations.Appointment.FridayClick, this.FridayClick);
        this.RegisterOperation(LocalPatientOperations.Appointment.WeekdayClick, this.WeekdayClick);
        this.RegisterOperation(LocalPatientOperations.Appointment.WeekendClick, this.WeekendClick);
        this.RegisterOperation(LocalPatientOperations.Appointment.AsapClick, this.AsapClick);
        this.RegisterOperation(LocalPatientOperations.Appointment.AddWeekClick, this.AddWeekClick);
        this.RegisterOperation(LocalPatientOperations.Appointment.AddMonthClick, this.AddMonthClick);
        this.RegisterOperation(LocalPatientOperations.Appointment.PMClick, this.PMCLick);
        this.RegisterOperation(LocalPatientOperations.Appointment.AMClick, this.AMClick);
        this.RegisterOperation(LocalPatientOperations.Appointment.IgnorePublishingRulesClick, this.IgnorePublishigRulesClick);
        this.RegisterOperation(LocalPatientOperations.Appointment.OnModifyClick, this.OnModifyClick);
        this.RegisterOperation(LocalPatientOperations.Appointment.OnOkEditClick, this.OnOkEditClick);
        this.RegisterOperation(LocalPatientOperations.Appointment.OnCancelModifiedAppoinment, this.OnCancelModifiedAppoinment);
        this.RegisterOperation(LocalPatientOperations.Appointment.OnChangeAppointmentSubject, this.SubjectChange);
        this.RegisterOperation(LocalPatientOperations.Appointment.OnSelectSubjectService, this.OnSelectSubjectService);
        this.RegisterOperation(LocalPatientOperations.Appointment.OnOkModifiedAppointment, this.OnOkModifiedAppointment);
        this.RegisterOperation(LocalPatientOperations.Appointment.OnDurationModifiedAppointmentChange, this.OnDurationModifiedAppointmentChange);
        this.RegisterOperation(LocalPatientOperations.Appointment.onAppointmentMount, this.onAppointmentMount);
        this.RegisterOperation(LocalPatientOperations.Appointment.OnServiceForEmployeeItemClick, this.OnServiceItemClick);
        this.RegisterOperation(LocalPatientOperations.Appointment.OnServiceForServiceItemClick, this.OnServiceForServiceItemClick);

        this.RegisterOperation(LocalPatientOperations.Appointment.on_ClinicianTab_AnyService_Click, this.onClinicianTabAnyServiceClick);
        this.RegisterOperation(LocalPatientOperations.Appointment.OnServiceDurationEdit, this.onServiceDurationEdit);
    }

    private onAppointmentMount(request: LocalEvent, store: PatientStore): void {
        this.defaultClinician = request.Get<Person>("defaultClinicianValue");

        var uiModel = store.PatientAppointmentModels[request.PatientId];

        if (uiModel.get("SearchCriteria") == null || uiModel.get("SearchCriteria").count() == 0) {
            uiModel = uiModel.set("ShowManagement", false);
            uiModel = uiModel.set("ShowCriteriaSelectionWindow", true);
        }

        if (uiModel.get("GlobalServiceOrComboList") == null || uiModel.get("GlobalServiceOrComboList").count() == 0)
            uiModel = this.loadServices(uiModel);

        if (uiModel.get("CliniciansGlobalCache") == null || uiModel.get("CliniciansGlobalCache").count() == 0)
            uiModel = this.loadServiceFinderClinicians(uiModel);

        let anyServiceList: AnyServiceModel[] = [];

        cacheStore.SiteCache.forEach(v => {
            let anyService = new AnyServiceModel();

            anyService.siteId = v.Model.Id;
            anyService.isSelected = false;
            anyService.anyExceptEmergencies = false;

            anyServiceList.push(anyService);
        });

        uiModel = uiModel.set("AnyServiceList", anyServiceList.toImmutable());

        store.PatientAppointmentModels[request.PatientId] = uiModel;

        const selectedClinician = uiModel.get("CliniciansFiltered").find(clinician => clinician.Checked);
        if (selectedClinician == undefined)
            this.SelectDefaultClinician(request, store);
    }

    private SelectDefaultClinician(request: LocalEvent, store: PatientStore): void {
        const uiModel = store.PatientAppointmentModels[request.PatientId];

        const defaultClinicianItem = uiModel.get("CliniciansFiltered").find(clinician => clinician.ContactFacetId == this.defaultClinician?.ContactFacetId);
        if (defaultClinicianItem == undefined) return;

        defaultClinicianItem.Checked = true;

        const operation = new LocalEvent(LocalPatientOperations.Appointment.OnClinicianTab_ClinicianSelected);
        operation.AddItem("checked", defaultClinicianItem.Checked);
        operation.AddItem("id", defaultClinicianItem.Id);
        operation.PatientId = request.PatientId;
        this.OnClinicianTab_ClinicianSelected(operation, patientStore);
    }

    private OnDurationModifiedAppointmentChange(request: LocalEvent, store: PatientStore): void {
        var uiModel = store.PatientAppointmentModels[request.PatientId];

        var requestItem = request.Get<PredefinedAppointmentItem>("item");
        var requestDuration = request.Get<number>("duration");

        var key = PatientAppointmentHandler.GetAppointmentModelKey(requestItem);

        uiModel = this.MutateSendingAppointmentsList(
            uiModel,
            key,
            (appointment: PredefinedAppointmentItem) => {
                appointment.Duration = requestDuration * 600000000;
            }
        );

        store.PatientAppointmentModels[request.PatientId] = uiModel;
    }

    public static GetAppointmentModelKey(pAptItem: PredefinedAppointmentItem): string {
        if (pAptItem.key != null)
            return pAptItem.key;
        else {
            return MyleHelper.GetStandardizedDateString(moment(pAptItem.Start).toDate()) + MyleHelper.GetStandardizedDateString(pAptItem.GetEnd());
        }
    }

    private OnOkModifiedAppointment(request: LocalEvent, store: PatientStore): void {
        var uiModel = store.PatientAppointmentModels[request.PatientId];

        var requestValue = request.Get<PredefinedAppointmentItem>("value");
        var key = PatientAppointmentHandler.GetAppointmentModelKey(requestValue);

        var uiModel = store.PatientAppointmentModels[request.PatientId];
        uiModel = this.MutateSendingAppointmentsList(
            uiModel,
            key,
            (appointment: PredefinedAppointmentItem) => {
                appointment.IsInEditMode = false;
            }
        );

        store.PatientAppointmentModels[request.PatientId] = uiModel;
    }

    private OnSelectSubjectService(request: LocalEvent, store: PatientStore): void {
        var serviceModel = request.Get<ServiceDefinitionModel>("serviceModel");
        var predefinedAppointment = request.Get<PredefinedAppointmentItem>("predefinedAppointmentInfo");
        var key = PatientAppointmentHandler.GetAppointmentModelKey(predefinedAppointment);
        var uiModel = store.PatientAppointmentModels[request.PatientId];

        if (uiModel.get("SelectedOptimizedResult")) {
            var optimizedResult = uiModel.get("SelectedOptimizedResult");
            optimizedResult.Subject = "";
            uiModel = uiModel.set("SelectedOptimizedResult", optimizedResult);
        }

        var predefinedItems = uiModel.get("SendingAppointmentsList");
        predefinedAppointment.Subject = "";
        predefinedItems[key] = predefinedAppointment;
        uiModel = uiModel.set("SendingAppointmentsList", predefinedItems);

        uiModel = uiModel.set("SelectedSubjectResourceDefinitionId", serviceModel.Model.Id);
        uiModel = this.MutateSendingAppointmentsList(
            uiModel,
            key,
            (appointment: PredefinedAppointmentItem) => {
                appointment.Name = serviceModel.Name;
                appointment.Subject = "";
                appointment.ServiceDefinitionId = serviceModel.Model.Id;
                appointment.IsInEditMode = false;

                if (appointment.IsFromPortalSearch != true) {
                    appointment.Duration = serviceModel.Model.DefaultDuration;
                }
            }
        );

        store.PatientAppointmentModels[request.PatientId] = uiModel;
    }

    private SubjectChange(request: LocalEvent, store: PatientStore): void {
        var customSubject = request.Get<string>("customSubject");
        var predefinedAppointment = request.Get<PredefinedAppointmentItem>("predefinedAppointmentInfo");

        var key = PatientAppointmentHandler.GetAppointmentModelKey(predefinedAppointment);

        var uiModel = store.PatientAppointmentModels[request.PatientId];

        uiModel = this.MutateSendingAppointmentsList(
            uiModel,
            key,
            (appointment: PredefinedAppointmentItem) => {
                appointment.Name = customSubject;
            }
        );

        store.PatientAppointmentModels[request.PatientId] = uiModel;
    }

    public MutateSendingAppointmentsList(uiModel: IPatientAppointmentModel, key: string, Mutation: (item: PredefinedAppointmentItem) => void): IPatientAppointmentModel {
        var list = uiModel.get("SendingAppointmentsList");
        Object.keys(list).map(listKey => {
            if (key === listKey) {
                var item = list[listKey];

                let newItem = JSON.parse(JSON.stringify(item));
                Mutation(newItem);

                var newkey = PatientAppointmentHandler.GetAppointmentModelKey(newItem);

                delete list[listKey];
                if (Object.keys(list).indexOf(newkey) === -1) {
                    list[newkey] = newItem;
                }
            }
        });
        uiModel = uiModel.set("SendingAppointmentsList", list);

        return uiModel;
    }

    private OnCancelModifiedAppoinment(request: LocalEvent, store: PatientStore): void {
        var requestValue = request.Get<PredefinedAppointmentItem>("value");
        var key = PatientAppointmentHandler.GetAppointmentModelKey(requestValue);
        var uiModel = store.PatientAppointmentModels[request.PatientId];

        uiModel = this.MutateSendingAppointmentsList(
            uiModel,
            key,
            (appointment: PredefinedAppointmentItem) => {
                appointment.IsInEditMode = false;
                if (appointment.DefaultName) {
                    appointment.Name = appointment.DefaultName;
                }
                if (appointment.DefaultDuration) {
                    appointment.Duration = appointment.DefaultDuration;
                }
                if (appointment.DefaultServiceDefinitionId) {
                    appointment.ServiceDefinitionId = appointment.DefaultServiceDefinitionId;
                }
            }
        );

        store.PatientAppointmentModels[request.PatientId] = uiModel;
    }

    private OnOkEditClick(request: LocalEvent, store: PatientStore): void {
        var uiModel = store.PatientAppointmentModels[request.PatientId];

        var isInEdit = uiModel.get("IsInEditMode");
        uiModel = uiModel.set("IsInEditMode", !isInEdit);

        store.PatientAppointmentModels[request.PatientId] = uiModel;
    }

    private OnModifyClick(request: LocalEvent, store: PatientStore): void {
        var uiModel = store.PatientAppointmentModels[request.PatientId];

        var requestValue = request.Get<PredefinedAppointmentItem>("value");

        var key = PatientAppointmentHandler.GetAppointmentModelKey(requestValue);

        uiModel = this.MutateSendingAppointmentsList(
            uiModel,
            key,
            (appointment: PredefinedAppointmentItem) => {
                appointment.IsInEditMode = true;
                appointment.Duration = requestValue.Duration;
                appointment.DefaultDuration = requestValue.Duration;
                appointment.DefaultName = requestValue.Name;
                appointment.DefaultServiceDefinitionId = requestValue.ServiceDefinitionId;

                // Add info
                uiModel = uiModel.set("Subject", appointment.Name);
            }
        );

        store.PatientAppointmentModels[request.PatientId] = uiModel;
    }

    private PMCLick(request: LocalEvent, store: PatientStore): void {
        let uiModel = store.PatientAppointmentModels[request.PatientId];

        const checked = uiModel.get("PMCheck");
        uiModel = uiModel.set("PMCheck", !checked);

        store.PatientAppointmentModels[request.PatientId] = uiModel;

        if (uiModel.get("SearchCriteria").count() > 0)
            store.ActionsAppointment.LoadPredefineAppointmentAvailability(uiModel, MyleHelper.GetStandardizedDateString(moment().utc().toDate()), request.PatientId);
    }

    private AMClick(request: LocalEvent, store: PatientStore): void {
        let uiModel = store.PatientAppointmentModels[request.PatientId];

        const checked = uiModel.get("AMCheck");
        uiModel = uiModel.set("AMCheck", !checked);

        store.PatientAppointmentModels[request.PatientId] = uiModel;

        if (uiModel.get("SearchCriteria").count() > 0)
            store.ActionsAppointment.LoadPredefineAppointmentAvailability(uiModel, MyleHelper.GetStandardizedDateString(moment().utc().toDate()), request.PatientId);
    }

    private AddMonthClick(request: LocalEvent, store: PatientStore): void {
        const uiModel = store.PatientAppointmentModels[request.PatientId];

        const value = request.Get<number>("value");

        const timeSlot = uiModel.get("TimeSlot");
        if (timeSlot.size > 0) {
            const datePlusOneMonth = LuxonDateTimeParser.parse(timeSlot.get(0).ControlDate).toUTC().plus({ months: value }).toJSDate();
            datePlusOneMonth.setHours(0);
            datePlusOneMonth.setMinutes(0);

            store.ActionsAppointment.LoadPredefineAppointmentAvailability(uiModel, MyleHelper.GetStandardizedDateString(datePlusOneMonth), request.PatientId);
        }
    }

    private AddWeekClick(request: LocalEvent, store: PatientStore): void {
        const uiModel = store.PatientAppointmentModels[request.PatientId];

        const value = request.Get<number>("value");

        const timeSlot = uiModel.get("TimeSlot");
        if (timeSlot.size > 0) {
            const datePlusOneWeek = LuxonDateTimeParser.parse(timeSlot.get(0).ControlDate).toUTC().plus({ weeks: value }).toJSDate();
            datePlusOneWeek.setHours(0);
            datePlusOneWeek.setMinutes(0);

            store.ActionsAppointment.LoadPredefineAppointmentAvailability(uiModel, MyleHelper.GetStandardizedDateString(datePlusOneWeek), request.PatientId);
        }
    }

    private AsapClick(request: LocalEvent, store: PatientStore): void {
        let uiModel = store.PatientAppointmentModels[request.PatientId];

        const value = request.Get<boolean>("includeRuleSets");

        uiModel = uiModel.set("PreviousFirstDate", Immutable.List([]));

        uiModel = uiModel.set("IncludeRuleSets", value);

        store.PatientAppointmentModels[request.PatientId] = uiModel;

        if (uiModel.get("SearchCriteria").count() > 0)
            store.ActionsAppointment.LoadPredefineAppointmentAvailability(uiModel, MyleHelper.GetStandardizedDateString(moment().utc().toDate()), request.PatientId);
    }

    private WeekendClick(request: LocalEvent, store: PatientStore): void {
        let uiModel = store.PatientAppointmentModels[request.PatientId];

        const checked = uiModel.get("WeekendCheck");
        uiModel = uiModel.set("WeekendCheck", !checked);

        store.PatientAppointmentModels[request.PatientId] = uiModel;

        if (uiModel.get("SearchCriteria").count() > 0)
            store.ActionsAppointment.LoadPredefineAppointmentAvailability(uiModel, MyleHelper.GetStandardizedDateString(moment().utc().toDate()), request.PatientId);
    }

    private WeekdayClick(request: LocalEvent, store: PatientStore): void {
        let uiModel = store.PatientAppointmentModels[request.PatientId];

        const checked = uiModel.get("WeekdayCheck");
        uiModel = uiModel.set("WeekdayCheck", !checked);

        uiModel = uiModel.set("MondayCheck", !checked);
        uiModel = uiModel.set("TuesdayCheck", !checked);
        uiModel = uiModel.set("WednesdayCheck", !checked);
        uiModel = uiModel.set("ThursdayCheck", !checked);
        uiModel = uiModel.set("FridayCheck", !checked);

        store.PatientAppointmentModels[request.PatientId] = uiModel;

        if (uiModel.get("SearchCriteria").count() > 0)
            store.ActionsAppointment.LoadPredefineAppointmentAvailability(uiModel, MyleHelper.GetStandardizedDateString(moment().utc().toDate()), request.PatientId);
    }

    private checkIfWeekdaysIsSelected(patientId: string, store: PatientStore): void {
        let uiModel = store.PatientAppointmentModels[patientId];

        if (uiModel.get("MondayCheck") === false || uiModel.get("TuesdayCheck") === false || uiModel.get("WednesdayCheck") === false || uiModel.get("ThursdayCheck") === false || uiModel.get("FridayCheck") === false) {
            uiModel = uiModel.set("WeekdayCheck", false);
        }
        if (uiModel.get("MondayCheck") === true && uiModel.get("TuesdayCheck") === true && uiModel.get("WednesdayCheck") === true && uiModel.get("ThursdayCheck") === true && uiModel.get("FridayCheck") === true) {
            uiModel = uiModel.set("WeekdayCheck", true);
        }

        store.PatientAppointmentModels[patientId] = uiModel;
    }

    private FridayClick(request: LocalEvent, store: PatientStore): void {
        let uiModel = store.PatientAppointmentModels[request.PatientId];

        const checked = uiModel.get("FridayCheck");
        uiModel = uiModel.set("FridayCheck", !checked);

        store.PatientAppointmentModels[request.PatientId] = uiModel;

        if (uiModel.get("SearchCriteria").count() > 0)
            store.ActionsAppointment.LoadPredefineAppointmentAvailability(uiModel, MyleHelper.GetStandardizedDateString(moment().utc().toDate()), request.PatientId);

        this.checkIfWeekdaysIsSelected(request.PatientId, store);
    }

    private ThursdayClick(request: LocalEvent, store: PatientStore): void {
        let uiModel = store.PatientAppointmentModels[request.PatientId];

        const checked = uiModel.get("ThursdayCheck");
        uiModel = uiModel.set("ThursdayCheck", !checked);

        store.PatientAppointmentModels[request.PatientId] = uiModel;

        if (uiModel.get("SearchCriteria").count() > 0)
            store.ActionsAppointment.LoadPredefineAppointmentAvailability(uiModel, MyleHelper.GetStandardizedDateString(moment().utc().toDate()), request.PatientId);

        this.checkIfWeekdaysIsSelected(request.PatientId, store);
    }

    private WednesdayClick(request: LocalEvent, store: PatientStore): void {
        let uiModel = store.PatientAppointmentModels[request.PatientId];

        const checked = uiModel.get("WednesdayCheck");
        uiModel = uiModel.set("WednesdayCheck", !checked);

        store.PatientAppointmentModels[request.PatientId] = uiModel;

        if (uiModel.get("SearchCriteria").count() > 0)
            store.ActionsAppointment.LoadPredefineAppointmentAvailability(uiModel, MyleHelper.GetStandardizedDateString(moment().utc().toDate()), request.PatientId);

        this.checkIfWeekdaysIsSelected(request.PatientId, store);
    }

    private TuesdayClick(request: LocalEvent, store: PatientStore): void {
        let uiModel = store.PatientAppointmentModels[request.PatientId];

        const checked = uiModel.get("TuesdayCheck");
        uiModel = uiModel.set("TuesdayCheck", !checked);

        store.PatientAppointmentModels[request.PatientId] = uiModel;

        if (uiModel.get("SearchCriteria").count() > 0)
            store.ActionsAppointment.LoadPredefineAppointmentAvailability(uiModel, MyleHelper.GetStandardizedDateString(moment().utc().toDate()), request.PatientId);

        this.checkIfWeekdaysIsSelected(request.PatientId, store);
    }

    private MondayClick(request: LocalEvent, store: PatientStore): void {
        let uiModel = store.PatientAppointmentModels[request.PatientId];

        const checked = uiModel.get("MondayCheck");
        uiModel = uiModel.set("MondayCheck", !checked);

        store.PatientAppointmentModels[request.PatientId] = uiModel;

        if (uiModel.get("SearchCriteria").count() > 0)
            store.ActionsAppointment.LoadPredefineAppointmentAvailability(uiModel, MyleHelper.GetStandardizedDateString(moment().utc().toDate()), request.PatientId);

        this.checkIfWeekdaysIsSelected(request.PatientId, store);
    }

    private IgnorePublishigRulesClick(request: LocalEvent, store: PatientStore): void {
        store.toggleIgnorePublicationRules();

        const uiModel = store.PatientAppointmentModels[request.PatientId];
        if (uiModel.get("SearchCriteria").count() > 0)
            store.ActionsAppointment.LoadPredefineAppointmentAvailability(uiModel, MyleHelper.GetStandardizedDateString(moment().utc().toDate()), request.PatientId);
    }

    private Reload(request: LocalEvent, store: PatientStore): void {
        const uiModel = store.PatientAppointmentModels[request.PatientId];
        const timeSlot = uiModel.get("TimeSlot");

        if (!uiModel.get("SearchCriteria").count())
            return;

        const currentDate = DateTime.fromJSDate(timeSlot.get(0)?.ControlDate);
        store.ActionsAppointment.LoadPredefineAppointmentAvailability(uiModel, MyleHelper.GetStandardizedDateString(currentDate.toJSDate()), request.PatientId);
    }

    private Previous(request: LocalEvent, store: PatientStore): void {
        const uiModel = store.PatientAppointmentModels[request.PatientId];
        const previousFirstDate = uiModel.get("PreviousFirstDate");

        if (uiModel.get("SearchCriteria").count() > 0)
            store.ActionsAppointment.LoadPredefineAppointmentAvailability(uiModel, MyleHelper.GetStandardizedDateString(previousFirstDate.last()), request.PatientId);

        store.PatientAppointmentModels[request.PatientId] = uiModel.set("PreviousFirstDate", previousFirstDate.pop());
    }

    private Next(request: LocalEvent, store: PatientStore): void {
        const uiModel = store.PatientAppointmentModels[request.PatientId];
        const previousFirstDate = uiModel.get("PreviousFirstDate");
        const timeSlot = uiModel.get("TimeSlot");

        if (uiModel.get("SearchCriteria").count() > 0) {
            let addOneDayDate = DateTime.fromJSDate(timeSlot.get(timeSlot.size - 1)?.ControlDate);
            addOneDayDate = addOneDayDate.plus({ day: 1 });
            addOneDayDate = addOneDayDate.set({ minute: 0, hour: 0 });
            store.ActionsAppointment.LoadPredefineAppointmentAvailability(uiModel, MyleHelper.GetStandardizedDateString(addOneDayDate.toJSDate()), request.PatientId);
        }

        store.PatientAppointmentModels[request.PatientId] = uiModel.set("PreviousFirstDate", previousFirstDate.push(timeSlot.get(0)?.ControlDate));
    }

    private ResetClick(request: LocalEvent): void {
        if (cacheStore.ClinicSettings.EnablePatientChartMultiServiceAvailabilitySearch)
            this.ResetClick_MultiServiceSearch(request);
        else
            this.ResetClick_SingleServiceSearch(request);
    }

    private ResetClick_SingleServiceSearch(request: LocalEvent): void {
        this.ClearCriterias(request.PatientId);
        this.ShowServiceFinderRaw(request.PatientId);
    }

    private ResetClick_MultiServiceSearch(request: LocalEvent): void {
        this.ClearCriterias(request.PatientId);
        this.ShowServiceFinderRaw(request.PatientId);
        this.ResetServiceFinderDefaults(request.PatientId);
        this.SelectDefaultClinician(request, patientStore);
    }

    public ClearCriterias(PatientId: string): void {
        var uiModel = patientStore.PatientAppointmentModels[PatientId];

        uiModel = uiModel.set("SearchCriteria", Immutable.List([]));
        uiModel = uiModel.set("TimeSlot", Immutable.List([]));

        uiModel = uiModel.set("CliniciansSearchText", "");
        uiModel = uiModel.set("ServicesSearchText", "");

        patientStore.PatientAppointmentModels[PatientId] = uiModel;
    }

    public ResetServiceFinderDefaults(PatientId: string): void {
        var uiModel = patientStore.PatientAppointmentModels[PatientId];

        uiModel = uiModel.set("IsAllEmployeeSelected", false);
        uiModel = uiModel.set("IsAllServiceSelected", false);

        uiModel = uiModel.set("CliniciansFiltered", Immutable.List([]));
        uiModel = uiModel.set("EmployeeServiceOrComboList", Immutable.List([]));

        uiModel = uiModel.set("ServicesClinicianList", Immutable.List([]));
        uiModel = uiModel.set("GlobalServiceOrComboList", uiModel.get("GlobalServiceOrComboList").map(p => { p.Checked = false; return p; }).toList());
        uiModel = uiModel.set("GlobalServiceOrComboFilteredList", Immutable.List([]));
        uiModel = uiModel.set("IsClinicianFilterTab", true);

        uiModel = this.loadServiceFinderClinicians(uiModel);

        patientStore.PatientAppointmentModels[PatientId] = uiModel;
    }

    private CancelSavePredefinedAppointment(request: LocalEvent, store: PatientStore): void {
        var uiModel = store.PatientAppointmentModels[request.PatientId];
        uiModel = uiModel.set("ShowSavePredefinedAppointment", false);
        store.PatientAppointmentModels[request.PatientId] = uiModel;
    }

    private OpenPredefinedAppointmentTimeSlot(request: LocalEvent, store: PatientStore): void {
        let uiModel = store.PatientAppointmentModels[request.PatientId];
        const blueRectangle = request.Get<PredefinedAppointmentGroup>("value");
        const predefinedAppointmentSaveState = PredefinedAppointmentSaveState.default();

        uiModel = uiModel.set("ShowSavePredefinedAppointment", true);

        if (blueRectangle.IsOptimizedSearch) {
            const optimizedResult = blueRectangle.OptmizedResult;
            const comboDefinition = cacheStore.GetComboDefinitionById(optimizedResult.ComboId);

            if (optimizedResult.ServiceBefore) {
                predefinedAppointmentSaveState.predefinedAppointments.push(
                    PredefinedAppointmentItem.FromServicePossibility(optimizedResult.ServiceBefore, comboDefinition)
                );
            }
            if (optimizedResult.ServiceMiddle) {
                predefinedAppointmentSaveState.predefinedAppointments.push(
                    PredefinedAppointmentItem.FromServicePossibility(
                        optimizedResult.ServiceMiddle,
                        comboDefinition,
                        optimizedResult.AnyService_PossibleServiceDefinitionIds // Add Possible ServiceDefinition if in "Any" mode;
                    )
                );
            }
            if (optimizedResult.ServiceAfter) {
                predefinedAppointmentSaveState.predefinedAppointments.push(
                    PredefinedAppointmentItem.FromServicePossibility(optimizedResult.ServiceAfter, comboDefinition)
                );
            }

            if (
                !optimizedResult.ComboId
                && predefinedAppointmentSaveState.predefinedAppointments.length === 1
            ) {
                // treat single-service ruleset availability like a predefined appointment so that the user can alter the service type or duration as-needed
                predefinedAppointmentSaveState.predefinedAppointmentType = PredefinedAppointmentType.PredefinedAppointment;
            }
            else {
                predefinedAppointmentSaveState.encryptedSaveKey = optimizedResult.EncryptedSaveKey;
                predefinedAppointmentSaveState.bookingTokenInfo = optimizedResult.BookingTokenInfo;

                predefinedAppointmentSaveState.predefinedAppointmentType = optimizedResult.AnyService_PossibleServiceDefinitionIds
                    ? PredefinedAppointmentType.OptimizedResultAny
                    : PredefinedAppointmentType.OptimizedResult;
            }
        }
        else {
            // Original Copy
            uiModel = uiModel.set("SavingAppointmentsList", blueRectangle.PredefinedAppointments);

            let increment = 0;
            Object.keys(blueRectangle.PredefinedAppointments).map((key: string) => {
                const searchCriteria = uiModel.get("SearchCriteria").toArray()[increment];
                const predef =
                    blueRectangle.PredefinedAppointments[key].firstOrDefault(appointment =>
                        searchCriteria.Model.ServiceList.some(service => service.Model.ServiceDefinitionId == appointment.ServiceDefinitionId)
                    )
                    ?? blueRectangle.PredefinedAppointments[key][0];

                predef.IsInEditMode = false;
                predef.IsFromPortalSearch = false;
                predefinedAppointmentSaveState.predefinedAppointmentType = PredefinedAppointmentType.PredefinedAppointment;

                const service = cacheStore.ServiceDefinitionCache.toArray().firstOrDefault(v => v.Model.Id === predef.ServiceDefinitionId);
                predef.IsTeleconsultation = service?.GetIsTeleconsultation() ?? false;
                predef.HasQuestionnaire = service?.GetQuestionnaireId() != null;
                predef.QuestionnaireId = service?.GetQuestionnaireId();
                predefinedAppointmentSaveState.predefinedAppointments.push(predef);

                increment = increment + 1;
            });
        }

        uiModel = uiModel.set("PredefinedAppointmentSaveState", predefinedAppointmentSaveState);

        store.PatientAppointmentModels[request.PatientId] = uiModel;
    }

    private Remove_SearchCriteria(request: LocalEvent, store: PatientStore): void {
        var uiModel = store.PatientAppointmentModels[request.PatientId];

        var criteria = request.Get<SearchCriteriaListItem>("criteria");

        var searchCriteriaList = uiModel.get("SearchCriteria").filter((n: SearchCriteriaListItem) => n.Model.Id !== criteria.Model.Id);
        uiModel = uiModel.set("SearchCriteria", searchCriteriaList.toList());

        store.PatientAppointmentModels[request.PatientId] = uiModel;

        if (searchCriteriaList.size > 0) {
            store.ActionsAppointment.LoadPredefineAppointmentAvailability(uiModel, MyleHelper.GetStandardizedDateString(moment().utc().toDate()), request.PatientId);
        }
        else {
            uiModel = uiModel.set("TimeSlot", Immutable.List([]));
            store.PatientAppointmentModels[request.PatientId] = uiModel;

            this.ResetClick(request);
        }
    }

    private OnClick_ServicesClinician(request: LocalEvent, store: PatientStore): void {
        var uiModel = store.PatientAppointmentModels[request.PatientId];

        var checked = request.Get<boolean>("checked");
        var id = request.Get<string>("id");

        var newClinicianList = uiModel.get("ServicesClinicianList").map(c => {
            if (c.Id === id) { c.Checked = checked; }
            return c;
        });

        uiModel = uiModel.set("ServicesClinicianList", newClinicianList.toList());
        store.PatientAppointmentModels[request.PatientId] = uiModel;
    }

    private OnOkClick(request: LocalEvent, store: PatientStore): void {
        if (cacheStore.ClinicSettings.EnablePatientChartMultiServiceAvailabilitySearch)
            this.OnOkClick_MultiServiceSearch(request, store);
        else
            this.OnOkClick_SingleServiceSearch(request, store);
    }

    private OnOkClick_SingleServiceSearch(request: LocalEvent, store: PatientStore): void {
        var uiModel = store.PatientAppointmentModels[request.PatientId];
        var criteria = uiModel.get("SearchCriteria").toArray();

        if (uiModel.get("IsClinicianFilterTab") === true) {
            var employeeList = uiModel.get("CliniciansGlobalCache").filter(e => e.Checked === true);
            if (employeeList.count() == 0)
                return; // Invalid Input

            var serviceList = uiModel.get("EmployeeServiceOrComboList").filter(s => s.Checked === true);
            // if (cacheStore.ClinicSettings_Portal.EnablePortalWebSite == true && serviceList.count() == 0)
            //    return; //Invalid Input

            var HasCombo = serviceList.some(x => x.Model.ComboId != null);
            if (HasCombo == true)
                criteria = []; // Cant combine a combo with other Criterias

            var anyServiceList = uiModel.get("AnyServiceList").toArray().filter(c => c.isSelected === true);

            let searchCriteria: SeacrhCriteriaItem = new SeacrhCriteriaItem();

            searchCriteria.Id = GuidHelper.NewGuid();
            searchCriteria.EmployeeList = employeeList.toArray();
            searchCriteria.ServiceList = serviceList.toArray();
            searchCriteria.IsFromService = false;
            searchCriteria.AnyServiceList = anyServiceList;

            var searchCriteriaList: SearchCriteriaListItem = new SearchCriteriaListItem(searchCriteria);

            criteria.push(searchCriteriaList);

            uiModel = uiModel.set("SearchCriteria", Immutable.List(criteria));
        }
        else {
            var serviceList = uiModel.get("GlobalServiceOrComboList").filter(s => s.Checked === true);
            if (serviceList.count() == 0)
                return; // Invalid Input

            var HasCombo = serviceList.some(x => x.Model.ComboId != null);
            if (HasCombo == true)
                criteria = []; // Cant combine a combo with other Criterias

            var employeeList = uiModel.get("ServicesClinicianList").filter(e => e.Checked === true);

            let searchCriteria: SeacrhCriteriaItem = new SeacrhCriteriaItem();

            searchCriteria.Id = GuidHelper.NewGuid();
            searchCriteria.EmployeeList = employeeList.toArray();
            searchCriteria.AllEmployees = employeeList.count() == uiModel.get("ServicesClinicianList").count();

            searchCriteria.ServiceList = serviceList.toArray();
            searchCriteria.IsFromService = true;

            var searchCriteriaList: SearchCriteriaListItem = new SearchCriteriaListItem(searchCriteria);

            criteria.push(searchCriteriaList);

            uiModel = uiModel.set("SearchCriteria", Immutable.List(criteria));
        }
        uiModel = uiModel.set("ShowManagement", true);
        uiModel = uiModel.set("ShowCriteriaSelectionWindow", false);

        store.PatientAppointmentModels[request.PatientId] = uiModel;

        store.ActionsAppointment.LoadPredefineAppointmentAvailability(uiModel, MyleHelper.GetStandardizedDateString(moment().utc().toDate()), request.PatientId);
    }

    private OnOkClick_MultiServiceSearch(request: LocalEvent, store: PatientStore): void {
        let uiModel = store.PatientAppointmentModels[request.PatientId];
        let criteria = uiModel.get("SearchCriteria").toArray();

        if (uiModel.get("IsClinicianFilterTab") === true) {
            const employeeList = uiModel.get("CliniciansGlobalCache").filter(e => e.Checked === true);
            if (employeeList.count() == 0)
                return; // Invalid Input

            const serviceList = uiModel.get("EmployeeServiceOrComboList").filter(s => s.Checked === true);

            serviceList.toArray().forEach(service => {
                let searchCriteria = new SeacrhCriteriaItem();

                searchCriteria.Id = GuidHelper.NewGuid();
                searchCriteria.EmployeeList = employeeList.toArray();
                searchCriteria.AllEmployees = false;
                searchCriteria.ServiceList = [service];
                searchCriteria.IsFromService = false;

                const searchCriteriaList = new SearchCriteriaListItem(searchCriteria);

                criteria.push(searchCriteriaList);
            });
        }
        else {
            const serviceList = uiModel.get("GlobalServiceOrComboList").filter(s => s.Checked === true);
            if (serviceList.count() == 0)
                return; // Invalid Input

            const employeeList = uiModel.get("ServicesClinicianList").filter(e => e.Checked === true);

            serviceList.toArray().forEach(service => {
                let searchCriteria = new SeacrhCriteriaItem();

                searchCriteria.Id = GuidHelper.NewGuid();
                searchCriteria.EmployeeList = employeeList.toArray();
                searchCriteria.AllEmployees = employeeList.count() == uiModel.get("ServicesClinicianList").count();
                searchCriteria.ServiceList = [service];
                searchCriteria.IsFromService = true;

                const searchCriteriaList = new SearchCriteriaListItem(searchCriteria);

                criteria.push(searchCriteriaList);
            });
        }

        uiModel = uiModel
            .set("SearchCriteria", Immutable.List(criteria))
            .set("ShowManagement", true)
            .set("ShowCriteriaSelectionWindow", false);

        store.PatientAppointmentModels[request.PatientId] = uiModel;

        store.ActionsAppointment.LoadPredefineAppointmentAvailability(uiModel, MyleHelper.GetStandardizedDateString(moment().utc().toDate()), request.PatientId);
    }

    private OnClick_ServiceTabAllEmployee(request: LocalEvent, store: PatientStore): void {
        var uiModel = store.PatientAppointmentModels[request.PatientId];

        var checked = request.Get<boolean>("checked");
        var employeeList = uiModel.get("ServicesClinicianList").toArray();

        uiModel = uiModel.set("IsAllEmployeeSelected", checked);
        if (checked === true) {
            for (let employee of employeeList) {
                employee.Checked = true;
            }
        }
        else {
            for (let employee of employeeList) {
                employee.Checked = false;
            }
        }
        uiModel = uiModel.set("ServicesClinicianList", Immutable.List(employeeList.sort((employee1, employee2) => employee1.LastName.trim().localeCompare(employee2.LastName.trim()))));

        store.PatientAppointmentModels[request.PatientId] = uiModel;
    }

    private OnClick_Services(request: LocalEvent, store: PatientStore): void {
        if (cacheStore.ClinicSettings.EnablePatientChartMultiServiceAvailabilitySearch)
            this.OnClick_MultiService(request, store);
        else
            this.OnClick_SingleService(request, store);
    }

    private OnClick_SingleService(request: LocalEvent, store: PatientStore): void {
        var uiModel = store.PatientAppointmentModels[request.PatientId];

        var checked = request.Get<boolean>("checked");
        var id = request.Get<string>("id");
        var serviceDefinitionId = request.Get<string>("serviceDefinitionId");

        var serviceListForUncheck = uiModel.get("GlobalServiceOrComboList");
        serviceListForUncheck.map((service: CheckboxUIModelT<ServiceOrComboTableItem<IComboOrServiceItem>>) => {
            if (service.Id != id)
                service.Checked = false;
            if (service.Id === id)
                uiModel = uiModel.set("CustomDuration", service.Model.Duration);
        });

        uiModel = uiModel.set("GlobalServiceOrComboList", this.helper.FindUpdateICheckableItem<CheckboxUIModelT<ServiceOrComboTableItem<IComboOrServiceItem>>>(serviceListForUncheck, id, checked));
        uiModel = uiModel.set("GlobalServiceOrComboFilteredList", this.getServicesBasedOnQuery(uiModel));

        var list: EmployeeUIModel[] = [];
        cacheStore.GetServiceDefinitionById(serviceDefinitionId).Employees.map(x => {
            var employee = EmployeeUIModel.fromCompositeObject(cacheStore.GetEmployeeByContactFacetId(x.ContactFacetId));
            if (employee != null) {
                employee.Target = CheckboxTarget.Myle_Patient_Appointment_ClinicianForServicesList;
                employee.Checked = false;
                list.push(employee);
            }
        });

        if (checked === true)
            uiModel = uiModel.set("ServicesClinicianList", Immutable.List(list.sort((employee1, employee2) => employee1.LastName.trim().localeCompare(employee2.LastName.trim()))));
        else
            uiModel = uiModel.set("ServicesClinicianList", Immutable.List([]));

        store.PatientAppointmentModels[request.PatientId] = uiModel;

        this.SelectServiceDefaultClinician(request, store);
    }

    private OnClick_MultiService(request: LocalEvent, store: PatientStore): void {
        const uiModel = store.PatientAppointmentModels[request.PatientId];

        const updatedServiceComboList = this.helper.FindUpdateICheckableItem<CheckboxUIModelT<ServiceOrComboTableItem<IComboOrServiceItem>>>(
            uiModel.get("GlobalServiceOrComboList"),
            request.Get<string>("id"),
            request.Get<boolean>("checked")
        );

        const selectedServices = updatedServiceComboList.filter(p => p.Checked).toArray();

        const customDuration = selectedServices.length === 1
            ? selectedServices[0].Model.Duration
            : uiModel.get("CustomDuration");

        const servicesClinicianList = uiModel.get("ServicesClinicianList").toArray();

        const updatedClinicianList = updatedServiceComboList
            .toArray()
            .filter(p => p.Checked)
            .flatMap(p => p.Model.EmployeeIds)
            .filter((id, idx, arr) => arr.indexOf(id) === idx)
            .map(id => {
                let employee = servicesClinicianList.firstOrDefault(p => p.ContactFacetId === id);

                // return the existing checkbox if it is already in the list
                if (employee)
                    return employee;

                employee = EmployeeUIModel.fromCompositeObject(cacheStore.GetEmployeeByContactFacetId(id));
                if (employee) {
                    employee.Target = CheckboxTarget.Myle_Patient_Appointment_ClinicianForServicesList;
                    employee.Checked = this.defaultClinician?.ContactFacetId === employee.ContactFacetId;
                }

                return employee;
            })
            .filter(Boolean)
            .sort((employee1, employee2) => employee1.LastName.trim().localeCompare(employee2.LastName.trim()));

        store.PatientAppointmentModels[request.PatientId] = uiModel
            .set("GlobalServiceOrComboList", updatedServiceComboList)
            .set("GlobalServiceOrComboFilteredList", this.getServicesBasedOnQuery(uiModel))
            .set("ServicesClinicianList", Immutable.List(updatedClinicianList))
            .set("CustomDuration", customDuration);
    }

    private SelectServiceDefaultClinician(request: LocalEvent, store: PatientStore): void {
        const uiModel = store.PatientAppointmentModels[request.PatientId];
        const serviceDefaultClinician = uiModel.get("ServicesClinicianList").find(clinician => clinician.ContactFacetId == this.defaultClinician?.ContactFacetId);
        if (serviceDefaultClinician != undefined) serviceDefaultClinician.Checked = true;
    }

    private OnClinicianTab_ClinicianSelected(request: LocalEvent, store: PatientStore): void {
        var uiModel = store.PatientAppointmentModels[request.PatientId];

        var checked = request.Get<boolean>("checked");
        var id = request.Get<string>("id");

        var clinicianListForUncheck = uiModel.get("CliniciansGlobalCache");
        clinicianListForUncheck.map((employee: EmployeeUIModel) => {
            if (employee.ContactId != id)
                employee.Checked = false;
        });

        uiModel = uiModel.set("CliniciansGlobalCache", this.helper.FindUpdateICheckableItem<EmployeeUIModel>(clinicianListForUncheck, id, checked));
        uiModel = uiModel.set("CliniciansFiltered", this.getCliniciansBasedOnQuery(uiModel));

        var serviceOrComboList = uiModel.get("GlobalServiceOrComboList");

        const result: CheckboxUIModelT<ServiceOrComboTableItem<IComboOrServiceItem>>[] = [];

        var services = cacheStore.GetEmployeeByContactIdOrEmpty(id).ServicesOffered.map(u => u.ServiceDefinitionModel.Model.Id);

        services.forEach(x => {
            serviceOrComboList.filter(y => y.Model.ServiceDefinitionId == x).forEach(z => {
                z.Checked = false;
                result.push(z);
            });
        });

        if (checked === true) {
            let anyServiceList = uiModel.get("AnyServiceList").toArray();
            if (anyServiceList.length > 0)
                anyServiceList[0].isSelected = true;

            uiModel = uiModel.set("CustomDuration", 10);

            const updatedAnyServiceList: AnyServiceModel[] = [];

            const defaultTab = sessionStore.getCurrentEmployee().getActiveSite().getId();

            anyServiceList.forEach(o => {
                if (o.siteId === defaultTab) {
                    o.isSelected = true;
                    o.anyExceptEmergencies = request.Get<boolean>("exceptUrgent");
                }
                else
                    o.isSelected = false;

                updatedAnyServiceList.push(o);
            });

            uiModel = uiModel.set("AnyServiceList", updatedAnyServiceList.toImmutable());

            uiModel = uiModel.set("ActiveTab", defaultTab);

            uiModel = uiModel.set("EmployeeServiceOrComboList", result.distinctByProperty(p => p.Id).toImmutable());
        }
        else
            uiModel = uiModel.set("EmployeeServiceOrComboList", Immutable.List([]));

        store.PatientAppointmentModels[request.PatientId] = uiModel;
    }

    private ShowServiceFinder(request: LocalEvent): void {
        let preserveValue = request.Get<boolean>("preserveValue");
        let IsAddSequenceService = request.Get<boolean>("IsAddSequenceService");

        if (IsAddSequenceService != true)
            this.ClearCriterias(request.PatientId);

        if (preserveValue != true)
            this.ResetServiceFinderDefaults(request.PatientId);

        this.ShowServiceFinderRaw(request.PatientId);
    }

    public ShowServiceFinderRaw(PatientId: string): void {
        var uiModel = patientStore.PatientAppointmentModels[PatientId];

        uiModel = uiModel.set("ShowManagement", false);
        uiModel = uiModel.set("ShowCriteriaSelectionWindow", true);

        patientStore.PatientAppointmentModels[PatientId] = uiModel;
    }

    private OnSearchTabSelected(request: LocalEvent, store: PatientStore): void {
        let uiModel = store.PatientAppointmentModels[request.PatientId];

        uiModel = uiModel.set("ActiveTab", request.Get<string>("activeTab"));

        let anyServiceList = uiModel.get("AnyServiceList").toArray();

        const updatedAnyServiceList: AnyServiceModel[] = [];

        anyServiceList.forEach(o => {
            if (o.siteId === uiModel.get("ActiveTab")) {
                o.isSelected = true;
                o.anyExceptEmergencies = request.Get<boolean>("exceptUrgent");
            }
            else
                o.isSelected = false;

            updatedAnyServiceList.push(o);
        });

        uiModel = uiModel.set("AnyServiceList", updatedAnyServiceList.toImmutable());

        store.PatientAppointmentModels[request.PatientId] = uiModel;
    }

    private ClinicianFilterChanged(request: LocalEvent, store: PatientStore): void {
        var uiModel = store.PatientAppointmentModels[request.PatientId];

        uiModel = uiModel.set("CliniciansSearchText", request.Get<string>("value"));
        uiModel = uiModel.set("CliniciansFiltered", this.getCliniciansBasedOnQuery(uiModel));

        store.PatientAppointmentModels[request.PatientId] = uiModel;
    }

    public getCliniciansBasedOnQuery(uiModel: IPatientAppointmentModel): Immutable.List<EmployeeUIModel> {
        var clinicians = uiModel.get("CliniciansGlobalCache");
        var filterString = $.trim(uiModel.get("CliniciansSearchText"));
        if (filterString !== "") { clinicians = SearchMenuHelper.FilterCliniciansByName(filterString, clinicians); }

        return clinicians;
    }

    private loadServiceFinderClinicians(uiModel: IPatientAppointmentModel): IPatientAppointmentModel {
        var list: EmployeeUIModel[] = [];
        for (let composite of cacheStore.EmployeeCache) {
            var employee = EmployeeUIModel.fromCompositeObject(composite);
            employee.Target = CheckboxTarget.Myle_Patient_Appointment_Clinicians;

            var services = employee.getServices();
            if (services?.any() && employee.ServicesCombos.some(s => !s.IsAdHoc))
                list.push(employee);
        }
        list = list.sort((employeeModel1, employeeModel2) => employeeModel1.LastName.trim().localeCompare(employeeModel2.LastName.trim()));
        uiModel = uiModel.set("CliniciansGlobalCache", Immutable.List(list));
        uiModel = uiModel.set("CliniciansFiltered", Immutable.List(list));
        return uiModel;
    }

    private HideServiceFinder(request: LocalEvent, store: PatientStore): void {
        var uiModel = store.PatientAppointmentModels[request.PatientId];
        uiModel = uiModel.set("ShowManagement", true);
        uiModel = uiModel.set("ShowCriteriaSelectionWindow", false);
        store.PatientAppointmentModels[request.PatientId] = uiModel;

        // Weird... doesnt register a change otherwise
        store.EmitChangeToListeners();
    }

    private ChangeTabToService(request: LocalEvent, store: PatientStore): void {
        var uiModel = store.PatientAppointmentModels[request.PatientId];
        uiModel = uiModel.set("IsClinicianFilterTab", false);
        uiModel = uiModel.set("GlobalServiceOrComboFilteredList", this.getServicesBasedOnQuery(uiModel));
        store.PatientAppointmentModels[request.PatientId] = uiModel;
    }

    private loadServices(uiModel: IPatientAppointmentModel): IPatientAppointmentModel {
        var servicesList = cacheStore.ServiceDefinitionCache.filter(x => x.Employees != null && x.Employees.length > 0 && x.IsAdHoc != true).toList();
        var comboList = cacheStore.ServiceDefinitionComboCache.filter(x => x.ServiceDefinitions.every(y => servicesList.some(z => z.Model.Id == y.Model.Id)));

        let result: CheckboxUIModelT<ServiceOrComboTableItem<IComboOrServiceItem>>[] = [];

        // aggregate service and combos together
        if (servicesList) {
            servicesList.forEach(x => {
                let serviceOrCombo: ServiceOrComboTableItem<IComboOrServiceItem> = new ServiceOrComboTableItem<ServiceDefinitionModel>(x);
                result.push(serviceOrCombo.ToCheckableUIModel());
            });
        }

        if (comboList) {
            comboList.forEach(x => {
                let serviceOrCombo: ServiceOrComboTableItem<IComboOrServiceItem> = new ServiceOrComboTableItem<ServiceComboDefinitionModel>(x);
                result.push(serviceOrCombo.ToCheckableUIModel());
            });
        }

        uiModel = uiModel.set("GlobalServiceOrComboList", result.toImmutable());
        uiModel = uiModel.set("GlobalServiceOrComboFilteredList", result.toImmutable());

        return uiModel;
    }

    private ServiceSearchChanged(request: LocalEvent, store: PatientStore): void {
        var uiModel = store.PatientAppointmentModels[request.PatientId];

        uiModel = uiModel.set("ServicesSearchText", request.Get<string>("value"));
        uiModel = uiModel.set("GlobalServiceOrComboFilteredList", this.getServicesBasedOnQuery(uiModel));

        store.PatientAppointmentModels[request.PatientId] = uiModel;
    }

    public getServicesBasedOnQuery(uiModel: IPatientAppointmentModel): Immutable.List<CheckboxUIModelT<ServiceOrComboTableItem<IComboOrServiceItem>>> {
        var services = uiModel.get("GlobalServiceOrComboList");
        var filterString = $.trim(uiModel.get("ServicesSearchText"));
        if (filterString !== "")
            services = services.toArray().filterSearchAdvanced(filterString, (IsMatchingSearchTextFct, x) => IsMatchingSearchTextFct(x.Name)).toImmutable();

        return services;
    }

    private ChangeTabToStaff(request: LocalEvent, store: PatientStore): void {
        var uiModel = store.PatientAppointmentModels[request.PatientId];

        uiModel = uiModel.set("IsClinicianFilterTab", true);

        store.PatientAppointmentModels[request.PatientId] = uiModel;
    }

    private ToggleMyleSchedulerDisplayMode(request: LocalEvent, store: PatientStore): void {
        var uiModel = store.PatientAppointmentModels[request.PatientId];
        var currentState = uiModel.get("IsSchedulerInStackMode");
        uiModel = uiModel.set("IsSchedulerInStackMode", !currentState);
        store.PatientAppointmentModels[request.PatientId] = uiModel;
    }

    private OnServiceItemClick(request: LocalEvent, store: PatientStore): void {
        if (cacheStore.ClinicSettings.EnablePatientChartMultiServiceAvailabilitySearch)
            this.OnServiceItemClick_MultiServiceSearch(request, store);
        else
            this.OnServiceItemClick_SingleServiceSearch(request, store);
    }

    private OnServiceItemClick_SingleServiceSearch(request: LocalEvent, store: PatientStore): void {
        let uiModel = store.PatientAppointmentModels[request.PatientId];
        let receivedId = request.Get<string>("value");
        let serviceList = uiModel.get("EmployeeServiceOrComboList").toArray();
        let updatedServiceList: CheckboxUIModelT<ServiceOrComboTableItem<IComboOrServiceItem>>[] = [];

        serviceList.forEach(n => {
            if (n.Id === receivedId) {
                n.Checked = true;
                uiModel = uiModel.set("CustomDuration", n.Model.Duration);
            }
            else
                n.Checked = false;
            updatedServiceList.push(n);
        });

        let anyServiceList = uiModel.get("AnyServiceList").toArray();
        let updatedAnyServiceList: AnyServiceModel[] = [];

        anyServiceList.forEach(o => {
            o.isSelected = false;

            updatedAnyServiceList.push(o);
        });

        uiModel = uiModel.set("EmployeeServiceOrComboList", updatedServiceList.toImmutable());
        uiModel = uiModel.set("AnyServiceList", updatedAnyServiceList.toImmutable());

        store.PatientAppointmentModels[request.PatientId] = uiModel;
    }

    private OnServiceItemClick_MultiServiceSearch(request: LocalEvent, store: PatientStore): void {
        const id = request.Get<string>("value");
        const checked = request.Get<boolean>("checked");

        const uiModel = store.PatientAppointmentModels[request.PatientId];

        const serviceList = uiModel
            .get("EmployeeServiceOrComboList")
            .map(service => {
                if (service.Id === id) {
                    service.Checked = checked;
                }

                return service;
            })
            .toArray();

        const selectedServices = serviceList.filter(p => p.Checked);

        const customDuration = selectedServices.length === 1
            ? selectedServices[0].Model.Duration
            : uiModel.get("CustomDuration");

        store.PatientAppointmentModels[request.PatientId] = uiModel
            .set("EmployeeServiceOrComboList", serviceList.toImmutable())
            .set("CustomDuration", customDuration);
    }

    private OnServiceForServiceItemClick(request: LocalEvent, store: PatientStore): void {
        var uiModel = store.PatientAppointmentModels[request.PatientId];

        var receivedId = request.Get<string>("value");
        var serviceList = uiModel.get("ServicesGlobalCache").toArray();
        var updatedServiceList: CheckboxUIModelT<ServiceDefinitionModel>[] = [];

        serviceList.forEach(n => {
            if (n.Id === receivedId) {
                n.Checked = !n.Checked;
            }

            updatedServiceList.push(n);
        });

        uiModel = uiModel.set("ServicesGlobalCache", serviceList.toImmutable());
        store.PatientAppointmentModels[request.PatientId] = uiModel;
    }

    private onClinicianTabAnyServiceClick(request: LocalEvent, store: PatientStore): void {
        if (cacheStore.ClinicSettings.EnablePatientChartMultiServiceAvailabilitySearch)
            this.onClinicianTabAnyServiceClick_MultiServiceSearch(request, store);
        else
            this.onClinicianTabAnyServiceClick_SingleServiceSearch(request, store);
    }

    private onClinicianTabAnyServiceClick_SingleServiceSearch(request: LocalEvent, store: PatientStore): void {
        var uiModel = store.PatientAppointmentModels[request.PatientId];
        var siteId = request.Get<string>("siteId");

        var serviceList = uiModel.get("EmployeeServiceOrComboList").toArray();
        var updatedServiceList: CheckboxUIModelT<ServiceOrComboTableItem<IComboOrServiceItem>>[] = [];

        serviceList.forEach(n => {
            n.Checked = false;
            updatedServiceList.push(n);
        });

        let anyServiceList = uiModel.get("AnyServiceList").toArray();
        let updateAnyServiceList: AnyServiceModel[] = [];

        anyServiceList.forEach(o => {
            if (o.siteId === siteId) {
                o.isSelected = true;
                o.anyExceptEmergencies = request.Get<boolean>("exceptUrgent");
            }
            else
                o.isSelected = false;

            updateAnyServiceList.push(o);
        });

        uiModel = uiModel.set("CustomDuration", 10);
        uiModel = uiModel.set("EmployeeServiceOrComboList", updatedServiceList.toImmutable());
        uiModel = uiModel.set("AnyServiceList", updateAnyServiceList.toImmutable());

        store.PatientAppointmentModels[request.PatientId] = uiModel;
    }

    private onClinicianTabAnyServiceClick_MultiServiceSearch(request: LocalEvent, store: PatientStore): void {
        const siteId = request.Get<string>("siteId");
        const exceptUrgent = request.Get<boolean>("exceptUrgent");
        const checked = request.Get<boolean>("checked");

        const uiModel = store.PatientAppointmentModels[request.PatientId];

        const serviceList = uiModel
            .get("EmployeeServiceOrComboList")
            .map(service => {
                if (service.Model.SiteId !== siteId)
                    return service;

                if (exceptUrgent)
                    service.Checked = checked && !service.Model.IsUrgent;
                else
                    service.Checked = checked;

                return service;
            })
            .toArray();

        store.PatientAppointmentModels[request.PatientId] = uiModel.set("EmployeeServiceOrComboList", serviceList.toImmutable());
    }

    private onServiceDurationEdit(request: LocalEvent, store: PatientStore): void {
        let uiModel = store.PatientAppointmentModels[request.PatientId];
        let duration = request.Get<number>("duration");

        uiModel = uiModel.set("CustomDuration", duration);
        store.PatientAppointmentModels[request.PatientId] = uiModel;
    }
}

export { PatientAppointmentHandler };
